import {
  useBlockProps,
  InspectorControls,
  MediaUpload,
  MediaUploadCheck
} from '@wordpress/block-editor'
import {
  PanelBody,
  TextControl,
  Button,
  ToggleControl,
  SelectControl,
  RangeControl
} from '@wordpress/components'
import { __ } from '@wordpress/i18n'

export default function Edit ({ attributes, setAttributes }) {
  const {
    imageId,
    imageUrl,
    imageAlt,
    title,
    titleSize,
    textTransform,
    linkUrl,
    linkTarget,
    titleAlign,
    titlePosition,
    imagePosition,
    imagePositionX,
    imagePositionY,
    cardStyle
  } = attributes
  const blockProps = useBlockProps({
    className: `coziehomes-card coziehomes-card--style-${cardStyle} coziehomes-card--title-${titleAlign} coziehomes-card--title-${titlePosition}`
  })

  const imageStyle =
    imagePosition === 'custom'
      ? { objectPosition: `${imagePositionX}% ${imagePositionY}%` }
      : { objectPosition: imagePosition }

  const onSelectImage = media => {
    setAttributes({
      imageId: media.id,
      imageUrl: media.url,
      imageAlt: media.alt || ''
    })
  }

  const onRemoveImage = () => {
    setAttributes({
      imageId: 0,
      imageUrl: '',
      imageAlt: ''
    })
  }

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Card Settings', 'coziehomes')}>
          <TextControl
            label={__('Title', 'coziehomes')}
            value={title}
            onChange={value => setAttributes({ title: value })}
            placeholder={__('Enter card title', 'coziehomes')}
          />
          <SelectControl
            label={__('Title Size', 'coziehomes')}
            value={titleSize}
            options={[
              { label: __('Base', 'coziehomes'), value: 'base' },
              { label: __('Small', 'coziehomes'), value: 'small' },
              { label: __('Medium', 'coziehomes'), value: 'medium' },
              { label: __('Large', 'coziehomes'), value: 'large' }
            ]}
            onChange={value => setAttributes({ titleSize: value })}
          />
          <SelectControl
            label={__('Text Transform', 'coziehomes')}
            value={textTransform}
            options={[
              { label: __('None', 'coziehomes'), value: 'none' },
              { label: __('Uppercase', 'coziehomes'), value: 'uppercase' },
              { label: __('Lowercase', 'coziehomes'), value: 'lowercase' },
              { label: __('Capitalize', 'coziehomes'), value: 'capitalize' }
            ]}
            onChange={value => setAttributes({ textTransform: value })}
          />
          <TextControl
            label={__('Link URL', 'coziehomes')}
            value={linkUrl}
            onChange={value => setAttributes({ linkUrl: value })}
            placeholder={__('https://example.com', 'coziehomes')}
            type='url'
          />
          <ToggleControl
            label={__('Open in new tab', 'coziehomes')}
            checked={linkTarget}
            onChange={value => setAttributes({ linkTarget: value })}
          />
          <SelectControl
            label={__('Title Alignment', 'coziehomes')}
            value={titleAlign}
            options={[
              { label: __('Left', 'coziehomes'), value: 'left' },
              { label: __('Center', 'coziehomes'), value: 'center' },
              { label: __('Right', 'coziehomes'), value: 'right' }
            ]}
            onChange={value => setAttributes({ titleAlign: value })}
          />
          <SelectControl
            label={__('Title Position', 'coziehomes')}
            value={titlePosition}
            options={[
              { label: __('Top', 'coziehomes'), value: 'top' },
              { label: __('Middle', 'coziehomes'), value: 'middle' },
              { label: __('Bottom', 'coziehomes'), value: 'bottom' }
            ]}
            onChange={value => setAttributes({ titlePosition: value })}
          />
          <SelectControl
            label={__('Card Style', 'coziehomes')}
            value={cardStyle}
            options={[
              { label: __('Style 1', 'coziehomes'), value: 'v1' },
              { label: __('Style 2', 'coziehomes'), value: 'v2' }
            ]}
            onChange={value => setAttributes({ cardStyle: value })}
          />
        </PanelBody>
        <PanelBody
          title={__('Image Position', 'coziehomes')}
          initialOpen={false}
        >
          <SelectControl
            label={__('Position', 'coziehomes')}
            value={imagePosition}
            options={[
              { label: __('Center', 'coziehomes'), value: 'center' },
              { label: __('Top', 'coziehomes'), value: 'top' },
              { label: __('Bottom', 'coziehomes'), value: 'bottom' },
              { label: __('Left', 'coziehomes'), value: 'left' },
              { label: __('Right', 'coziehomes'), value: 'right' },
              { label: __('Top Left', 'coziehomes'), value: 'top left' },
              { label: __('Top Right', 'coziehomes'), value: 'top right' },
              { label: __('Bottom Left', 'coziehomes'), value: 'bottom left' },
              {
                label: __('Bottom Right', 'coziehomes'),
                value: 'bottom right'
              },
              { label: __('Custom', 'coziehomes'), value: 'custom' }
            ]}
            onChange={value => setAttributes({ imagePosition: value })}
          />
          {imagePosition === 'custom' && (
            <>
              <RangeControl
                label={__('Horizontal (%)', 'coziehomes')}
                value={imagePositionX}
                onChange={value => setAttributes({ imagePositionX: value })}
                min={0}
                max={100}
              />
              <RangeControl
                label={__('Vertical (%)', 'coziehomes')}
                value={imagePositionY}
                onChange={value => setAttributes({ imagePositionY: value })}
                min={0}
                max={100}
              />
            </>
          )}
        </PanelBody>
      </InspectorControls>
      <div {...blockProps}>
        <MediaUploadCheck>
          <MediaUpload
            onSelect={onSelectImage}
            allowedTypes={['image']}
            value={imageId}
            render={({ open }) => (
              <>
                {imageUrl ? (
                  <>
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      className='coziehomes-card__img'
                      style={imageStyle}
                      onClick={open}
                      role='presentation'
                    />
                    <Button
                      onClick={onRemoveImage}
                      isDestructive
                      size='small'
                      variant='secondary'
                      className='coziehomes-card__remove-btn'
                    >
                      {__('Remove', 'coziehomes')}
                    </Button>
                  </>
                ) : (
                  <div
                    className='coziehomes-card__placeholder'
                    onClick={open}
                    role='button'
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && open()}
                  >
                    <Button
                      size='small'
                      variant='primary'
                      className='coziehomes-card__upload-btn'
                    >
                      {__('Select Image', 'coziehomes')}
                    </Button>
                  </div>
                )}
              </>
            )}
          />
        </MediaUploadCheck>

        {title ? (
          <h4
            className={`coziehomes-card__title coziehomes-card__title--transform-${textTransform} coziehomes-card__title--size-${titleSize}`}
          >
            {title}
          </h4>
        ) : (
          <h4 className='coziehomes-card__title coziehomes-card__title--placeholder'>
            {__('Add a title…', 'coziehomes')}
          </h4>
        )}
      </div>
    </>
  )
}
